// Pre-run flow (owner 2026-11-01): Title → START → CHARACTER SELECT → MAP SELECT (src/app/mapselect.ts) → RUN SETUP → run.
// DOM screens driven by UiActions (controller-first, vertical focus), with click/tap support. No gameplay rules here:
// the chosen options are handed to startRun(), which passes them to the Sim through SimOptions.
import { AUTO_SURGE_PATIENCE } from '../core/skillStats';
import { MODS, RunMods, DIFFICULTIES, DifficultyId, SetupMods, difficultyDef, modShardBonus, modsToString, bonusLabel, restoreSetup, selectDifficulty, stepDifficulty, toggleMod } from '../core/mods'; // run modifiers + difficulty presets (owner 2026-11-02)
import { CHARACTERS, CharacterDef, kitLine, LOCKED_SLOTS } from './characters';
import type { UiAction } from './input';
import { gameSpeedOf } from './settingsPrefs';
import { SURVIVAL_RUN } from './runDefaults';

export interface PreRunHost {
  click(): void;
  deny(): void;
  /** switch the screen state machine */
  show(s: 'charsel' | 'mapselect' | 'runsetup' | 'title'): void;
  start(): void;
  /** live 3D close-up of the fighter behind the select screen */
  previewHero(on: boolean): void;
  /** the map + time of day picked on the map select page (shown in the Run Setup header) */
  mapLine(): string;
  get(k: string): string | null;
  set(k: string, v: string): void;
  /** current controller / keyboard label for an action (for descriptions) */
  label(action: 'surge'): string;
  /** POWER UPS unlocks (2026-11-02): extra option rows (starting skill / trait, banishes), shown after the option rows */
  extraRows?(): ExtraRow[];
}

/** a Run Setup row whose options are built on the fly (POWER UPS unlocks); its value is stored with host.set(key) */
export interface ExtraRow { key: string; label: string; options(): { value: string; text: string }[]; desc(value: string): string }

export interface RunChoices { character: string; autoTransform: boolean; autoSkills: boolean; powerUps: boolean; bossTime: number; endless: true;
  /** Game speed, percent (70 / 85 / 100): fixed for the whole run (main.ts scales real time into the fixed-step sim) */ gameSpeed: number;
  /** run modifiers (Run Setup 'Modifiers' panel): handicaps that add to the Fracture Shards earned */
  mods: RunMods,
  /** the Difficulty row as left: a preset, or 'custom' after a hand edit (recorded with the run) */ difficulty: DifficultyId }

type Row = { id: string; key: string; label: string; desc: () => string };
const $ = <T extends HTMLElement>(s: string) => document.querySelector(s) as T;

export class PreRun {
  charSel = 0;
  /** character screen focus: 0 roster, 1 continue, 2 back */
  charFocus = 0;
  setupSel = 0;
  private readonly rows: Row[];
  /** run modifiers (owner 2026-11-02): checkbox rows after the option rows; remembered on the device */
  mods: RunMods = {};
  /** Difficulty row (owner 2026-11-02): one-tap bundles of the modifiers; any hand edit of a checkbox switches it to Custom */
  difficulty: DifficultyId = 'normal';
  /** row 0 is Difficulty, then the option rows, then the POWER UPS unlock rows, then one row per modifier */
  private get xIdx(): number { return this.rows.length + 1; }
  private get modIdx(): number { return this.xIdx + this.xrows.length; }
  /** index of the START RUN row; BACK follows it */
  private get goIdx(): number { return this.modIdx + MODS.length; }
  /** POWER UPS unlock rows (empty while nothing is unlocked or Power Ups is Off) */
  private get xrows(): ExtraRow[] { return this.host.extraRows?.() ?? []; }
  /** the current value of an extra row (the stored one while it is still offered, else the first option) */
  xvalue(r: ExtraRow): string {
    const opts = r.options(), v = this.host.get(r.key);
    return opts.some((o) => o.value === v) ? v! : opts[0]?.value ?? '';
  }
  private get setup(): SetupMods { return { difficulty: this.difficulty, mods: this.mods }; }
  private setSetup(n: SetupMods): void {
    this.difficulty = n.difficulty; this.mods = n.mods;
    this.host.set('runmods', modsToString(n.mods)); this.host.set('rundiff', n.difficulty);
  }

  constructor(private host: PreRunHost) {
    this.rows = [
      { id: 'autotf', key: 'autosurge', label: 'Surge Form', desc: () => this.sel('autotf').value === 'on'
        ? `Auto: you transform by yourself once the Surge bar is charged to your highest unlocked form and foes are close (or a boss is up). Past form III it also goes once the next form has not charged for ${AUTO_SURGE_PATIENCE} s.`
        : `Manual: transform when you choose (${this.host.label('surge')}, or the SURGE button on touch).` },
      { id: 'gamespeed', key: 'gamespeed', label: 'Game speed', desc: () => this.sel('gamespeed').value === '100'
        ? 'Normal speed. Lower it for more time to react; it stays the same for the whole run.'
        : `Everything runs at ${this.sel('gamespeed').value}%: foes, attacks and the run clock. Fixed for this run; a SPEED tag shows on the HUD.` },
      { id: 'powerups', key: 'powerups', label: 'Power Ups', desc: () => this.sel('powerups').value === 'off'
        ? 'Off: start with base stats for a challenge. Fracture Shards are still earned.'
        : 'On: your Power Ups ranks (Fracture Shard upgrades) apply to this run.' },
    ];
    // restore the last choices on this device
    for (const r of this.rows) { const v = host.get(r.key); const s = this.sel(r.id); if (v !== null && [...s.options].some((o) => o.value === v)) s.value = v; }
    { const r = restoreSetup(host.get('rundiff'), host.get('runmods')); this.mods = r.mods; this.difficulty = r.difficulty; }
    const c = CHARACTERS.findIndex((x) => x.id === host.get('hero')); this.charSel = c >= 0 ? c : 0;
    $('#cs-roster').addEventListener('click', (e) => {
      const t = (e.target as HTMLElement).closest('.cs-tile') as HTMLElement | null; if (!t) return;
      const i = Number(t.dataset.i); this.charFocus = 0;
      if (i === this.charSel) this.pickChar(); else { this.charSel = i; this.host.click(); this.renderChar(); }
    });
    $('#cs-go').addEventListener('click', () => this.pickChar());
    $('#cs-back').addEventListener('click', () => this.host.show('title'));
    $('#rs-list').addEventListener('click', (e) => {
      const t = e.target as HTMLElement, row = t.closest('.srow') as HTMLElement | null; if (!row) return;
      const i = Number(row.dataset.i); this.setupSel = i;
      const seg = t.closest('.dseg') as HTMLElement | null;
      if (i === 0) { // Difficulty row: tap a preset (the arrows / keys step through the four presets)
        if (seg) this.setSetup(selectDifficulty(this.setup, seg.dataset.d as DifficultyId)); this.host.click(); this.renderSetup(); return;
      }
      if (i === this.goIdx) { this.host.start(); return; }
      if (i === this.goIdx + 1) { this.host.show('mapselect'); return; }
      const arrow = t.closest('.sa') as HTMLElement | null;
      this.change(i, arrow ? Number(arrow.dataset.d) : 1);
    });
  }

  private sel(id: string) { return $<HTMLSelectElement>('#' + id); }
  get character(): CharacterDef { return CHARACTERS[this.charSel] ?? CHARACTERS[0]; }

  /** Normal survival policy is fixed; legacy run-length/endless preferences are deliberately ignored. */
  choices(): RunChoices {
    return {
      character: this.character.id,
      autoTransform: this.sel('autotf').value === 'on', autoSkills: true, // Nova Lance always fires itself (owner 2026-11-02: no manual option)
      powerUps: this.sel('powerups').value !== 'off', ...SURVIVAL_RUN,
      gameSpeed: gameSpeedOf(this.sel('gamespeed').value),
      mods: { ...this.mods }, difficulty: this.difficulty,
    };
  }

  // ---------------- character select ----------------
  openChar(): void { this.charFocus = 0; this.host.show('charsel'); }
  /** called by setScreen when the character screen opens / closes */
  enterChar(on: boolean): void { this.host.previewHero(on); if (on) this.renderChar(); }
  renderChar(): void {
    const n = CHARACTERS.length + LOCKED_SLOTS;
    let tiles = '';
    for (let i = 0; i < n; i++) {
      const c = CHARACTERS[i];
      tiles += c
        ? `<button class="cs-tile${i === this.charSel ? ' sel' : ''}${i === this.charSel && this.charFocus === 0 ? ' focus' : ''}" data-i="${i}" aria-label="${c.name}"><span class="cs-face cs-${c.id}"></span><span class="cs-tn">${c.name}</span></button>`
        : `<button class="cs-tile locked${i === this.charSel ? ' sel' : ''}${i === this.charSel && this.charFocus === 0 ? ' focus' : ''}" data-i="${i}" aria-label="Locked"><span class="cs-face">?</span><span class="cs-tn">???</span></button>`;
    }
    $('#cs-roster').innerHTML = tiles;
    const c = CHARACTERS[this.charSel];
    const card = $('#cs-card');
    if (c) {
      const inn = kitLine(c.innate), st = kitLine(c.start);
      card.classList.remove('locked');
      card.innerHTML = `<div class="cs-name">${c.name}</div><div class="cs-role">${c.role}</div><p class="cs-blurb">${c.blurb}</p>`
        + `<div class="cs-kit"><div><b>INNATE</b><span>${inn.name}</span><small>${inn.desc}</small></div><div><b>STARTS WITH</b><span>${st.name}</span><small>${st.desc}</small></div></div>`
        + `<div class="cs-stats">Health <b>${c.stats.hp}</b> · Power Ups add to it in a run</div>`;
    } else {
      card.classList.add('locked');
      card.innerHTML = '<div class="cs-name">???</div><div class="cs-role">Not yet available</div><p class="cs-blurb">More Conductors are planned. This slot unlocks when one is added.</p>';
    }
    $<HTMLButtonElement>('#cs-go').classList.toggle('focus', this.charFocus === 1);
    $<HTMLButtonElement>('#cs-go').disabled = !c;
    $<HTMLButtonElement>('#cs-back').classList.toggle('focus', this.charFocus === 2);
  }
  private pickChar(): void {
    if (!CHARACTERS[this.charSel]) { this.host.deny(); return; }
    this.host.set('hero', CHARACTERS[this.charSel].id); this.host.click();
    this.setupSel = this.goIdx; // land on START RUN: one more press starts with the remembered setup
    this.host.show('mapselect');
  }
  handleChar(a: UiAction): void {
    const n = CHARACTERS.length + LOCKED_SLOTS;
    if (a === 'back' || a === 'pause') { this.host.show('title'); return; }
    if (a === 'left' || a === 'right') {
      if (this.charFocus !== 0) { this.charFocus = 0; } else this.charSel = (this.charSel + (a === 'right' ? 1 : n - 1)) % n;
      this.host.click();
    } else if (a === 'down') { this.charFocus = Math.min(2, this.charFocus + 1); this.host.click(); }
    else if (a === 'up') { this.charFocus = Math.max(0, this.charFocus - 1); this.host.click(); }
    else if (a === 'confirm') { if (this.charFocus === 2) this.host.show('title'); else this.pickChar(); return; }
    this.renderChar();
  }

  // ---------------- run setup ----------------
  enterSetup(): void { this.renderSetup(); }
  renderSetup(): void {
    const c = this.character;
    if (this.setupSel > this.goIdx + 1) this.setupSel = this.goIdx; // POWER UPS rows can come and go
    $('#rs-hero').textContent = `${c.name} · ${this.host.mapLine()}`;
    // owner 2026-11-02: 'all the settings … on one static page, no scrolling': options | modifiers side by side (stacked on
    // portrait), modifiers as a 2-column grid of compact rows, START / BACK side by side. Navigation order is unchanged.
    let html = `<div class="srow diff${this.setupSel === 0 ? ' focus' : ''}" data-i="0" role="radiogroup" aria-label="Difficulty"><span class="sl">Difficulty</span><span class="dsegs">`
      + DIFFICULTIES.map((d) => `<button class="dseg ${d.id}${d.id === this.difficulty ? ' on' : ''}" data-d="${d.id}" role="radio" aria-checked="${d.id === this.difficulty}">${d.name}</button>`).join('')
      + '</span></div>';
    html += '<div class="rs-cols"><div class="rs-opts">';
    this.rows.forEach((r, ri) => {
      const i = ri + 1, s = this.sel(r.id), val = s.options[s.selectedIndex]?.text ?? '';
      html += `<div class="srow${i === this.setupSel ? ' focus' : ''}" data-i="${i}"><span class="sl">${r.label}</span>`
        + `<span class="sv"><button class="sa" data-d="-1" aria-label="previous">◀</button><b>${val}</b><button class="sa" data-d="1" aria-label="next">▶</button></span></div>`;
    });
    const xr = this.xrows; // POWER UPS unlocks: starting skill / trait, banishes — a compact 2-column block heading the right-hand column
    // (under the options on portrait), so Run Setup stays one static page
    let pu = '';
    if (xr.length) {
      pu += '<div class="rs-pubox"><div class="rs-modhead" role="presentation"><span>POWER UPS UNLOCKS</span></div><div class="rs-pugrid">';
      xr.forEach((r, k) => {
        const i = this.xIdx + k, v = this.xvalue(r), val = r.options().find((o) => o.value === v)?.text ?? '';
        pu += `<div class="srow pu${i === this.setupSel ? ' focus' : ''}" data-i="${i}"><span class="sl">${r.label}</span>`
          + `<span class="sv"><button class="sa" data-d="-1" aria-label="previous">◀</button><b>${val}</b><button class="sa" data-d="1" aria-label="next">▶</button></span></div>`;
      });
      pu += '</div></div>';
    }
    // run modifiers (owner 2026-11-02): a 'Modifiers' panel of checkbox rows; each shows its shard bonus, the header the total
    const bonus = modShardBonus(this.mods), r0 = this.modIdx;
    html += `</div><div class="rs-modbox">${pu}`;
    html += `<div class="rs-modhead" role="presentation"><span>MODIFIERS</span><b>${bonus !== 0 ? `${bonusLabel(bonus)} ◆ shards` : 'none · no bonus'}</b></div>`;
    html += '<div class="rs-modgrid">';
    MODS.forEach((m, k) => {
      const i = r0 + k, on = !!this.mods[m.id];
      html += `<div class="srow mod${on ? ' on' : ''}${i === this.setupSel ? ' focus' : ''}" data-i="${i}" role="checkbox" aria-checked="${on}">`
        + `<span class="sl"><span class="ck" aria-hidden="true"></span>${m.name} <em>${bonusLabel(m.bonus)}</em></span><span class="sv"><b>${on ? 'ON' : 'OFF'}</b></span></div>`;
    });
    html += '</div></div></div><div class="rs-actions">';
    const n = this.goIdx;
    html += `<div class="srow go${this.setupSel === n ? ' focus' : ''}" data-i="${n}"><span class="sl">START RUN${bonus !== 0 ? ` · ${bonusLabel(bonus)} ◆` : ''}</span></div>`;
    html += `<div class="srow back${this.setupSel === n + 1 ? ' focus' : ''}" data-i="${n + 1}"><span class="sl">BACK</span></div>`;
    html += '</div>';
    $('#rs-list').innerHTML = html;
    const md = MODS[this.setupSel - r0];
    const dd = difficultyDef(this.difficulty);
    const xs = this.setupSel >= this.xIdx && this.setupSel < r0 ? xr[this.setupSel - this.xIdx] : undefined;
    $('#rs-desc').textContent = this.setupSel === 0 ? `${dd.name}: ${this.difficulty === 'custom' ? `${dd.desc} Now ${bonus !== 0 ? bonusLabel(bonus) + ' shards' : 'no shard bonus'}.` : dd.desc}`
      : this.rows[this.setupSel - 1]?.desc() ?? (xs ? xs.desc(this.xvalue(xs)) : undefined)
      ?? (md ? `${md.desc} Shards ${bonusLabel(md.bonus)} (all picks: ${bonus !== 0 ? bonusLabel(bonus) : 'none'}).`
        : this.setupSel === n ? `Survive as long as you can as ${c.name}. Defeating the final boss never ends the run.${this.difficulty !== 'normal' ? ` Difficulty: ${dd.name}.` : ''}${bonus !== 0 ? ` Modifiers: ${bonusLabel(bonus)} Fracture Shards at the end of the run.` : ''}` : 'Back to the arena select.');
    $('#rs-list .srow.focus')?.scrollIntoView({ block: 'nearest' });
  }
  private change(i: number, d: number): void {
    if (i === 0) { this.setSetup(stepDifficulty(this.setup, d)); this.host.click(); this.renderSetup(); return; }
    const xr = this.xrows, x = i >= this.xIdx && i < this.modIdx ? xr[i - this.xIdx] : undefined;
    if (x) { // POWER UPS unlock row: cycle its options
      const opts = x.options(), n = opts.length; if (!n) return;
      const at = Math.max(0, opts.findIndex((o) => o.value === this.xvalue(x)));
      this.host.set(x.key, opts[(at + d + n) % n].value); this.host.click();
      this.renderSetup(); return;
    }
    const md = MODS[i - this.modIdx];
    if (md && i >= this.modIdx) { // modifier row: any change toggles it, and a hand edit makes the row Custom
      this.setSetup(toggleMod(this.setup, md.id)); this.host.click();
      this.renderSetup(); return;
    }
    const r = this.rows[i - 1]; if (!r) return;
    const s = this.sel(r.id), n = s.options.length;
    s.selectedIndex = (s.selectedIndex + d + n) % n;
    this.host.set(r.key, s.value); this.host.click();
    this.renderSetup();
  }
  /** the nearest box beside the focused one in the same row (left / right), by on-screen position; -1 if none */
  private sideways(a: 'left' | 'right'): number {
    const rows = [...document.querySelectorAll<HTMLElement>('#rs-list .srow[data-i]')], cur = rows.find((r) => Number(r.dataset.i) === this.setupSel);
    if (!cur) return -1;
    const c = cur.getBoundingClientRect(), cx = c.left + c.width / 2, cy = c.top + c.height / 2;
    let best = -1, bd = Infinity;
    for (const r of rows) {
      if (r === cur) continue;
      const q = r.getBoundingClientRect(), dx = q.left + q.width / 2 - cx, dy = q.top + q.height / 2 - cy;
      if (Math.abs(dy) > c.height * 0.5 || (a === 'right' ? dx <= 1 : dx >= -1)) continue;
      if (Math.abs(dx) < bd) { bd = Math.abs(dx); best = Number(r.dataset.i); }
    }
    return best;
  }
  handleSetup(a: UiAction): void {
    const n = this.goIdx + 2;
    if (a === 'back' || a === 'pause') { this.host.show('mapselect'); return; }
    if (a === 'down') { this.setupSel = (this.setupSel + 1) % n; this.host.click(); }
    else if (a === 'up') { this.setupSel = (this.setupSel + n - 1) % n; this.host.click(); }
    else if (a === 'left' || a === 'right') {
      // owner 2026-11-03: 'let me go left and right with the D-pad': on the modifier grid and START | BACK, left / right MOVE
      // to the neighbouring box; rows with a value (difficulty, options, power-up picks) still change it with left / right
      if (this.setupSel >= this.modIdx) { const j = this.sideways(a); if (j >= 0) { this.setupSel = j; this.host.click(); } }
      else { this.change(this.setupSel, a === 'right' ? 1 : -1); return; }
    }
    else if (a === 'tabPrev' || a === 'tabNext') { // L1 / R1 (no tabs here): jump between the left and right columns
      const right = this.xrows.length ? this.xIdx : this.modIdx;
      this.setupSel = this.setupSel >= right && this.setupSel < this.goIdx ? 1 : right; this.host.click();
    }
    else if (a === 'confirm') {
      if (this.setupSel === this.goIdx) { this.host.start(); return; }
      if (this.setupSel === this.goIdx + 1) { this.host.show('mapselect'); return; }
      this.change(this.setupSel, 1); return;
    }
    this.renderSetup();
  }
}
